import React, { useEffect } from 'react';
import { Fab, Tooltip, Typography } from '@mui/material';
import { AppDispatch, ExperimentEditorContainer, addInstrumentData, getExperimentList, openExperiment, saveExperiment, selectAllPlateWellConfigs, updateAnalysisViewSettings, updateWell, useAppDispatch, useAppSelector } from 'quil';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import UploadRpck, { UploadCytekFcs } from '../api/RpckApi';
import { RpckUploadStatus, WellReadStatus } from 'quanterix-protos/type/rpck_types';
import { dataMgr } from '../api/DataManagementApi';
import { RpckJobStatus } from 'quanterix-protos/shared/v1/rpck';
import { InstrumentDataBody } from 'quanterix-protos/run_data/v1/db_instrument_data';
import { WellReadStatus as WellReadStatusV2 } from '../gen/quanterix/common/types/v1/well_read_status_pb';
import { PlateWellConfig } from '../gen/quanterix/simoa_one/analysis_app_api/v1/plate_well_config_pb';
import { SampleType } from '../gen/quanterix/common/types/v1/sample_type_pb';
import { useLocation, useNavigate } from 'react-router-dom';

interface FeatureToggleProps{
  enabled: boolean;
  handleToggle: () => void;
}

export function FeatureFlagToggle({enabled, handleToggle}: FeatureToggleProps) {
  const tooltipContent = () => <Typography sx={{whiteSpace: 'pre-line'}}>Toggle Experiment Editor Feature Preview</Typography>

  return     (  
  <Tooltip title={tooltipContent()} >
    <Fab color={enabled ? "warning": "default"} sx={{position: 'absolute', top: 72, left: 72}} onClick={handleToggle}>
      {enabled ? <ToggleOnIcon/> : <ToggleOffIcon/>}
    </Fab>
  </Tooltip>
);
}

function wellReadStatusMap(w?: WellReadStatus): WellReadStatusV2 | undefined {
  switch (w) {
    case WellReadStatus.WELL_READ_STATUS_UNSPECIFIED:
      return WellReadStatusV2.UNSPECIFIED;
    case WellReadStatus.WELL_READ_STATUS_ERROR:
      return WellReadStatusV2.ERROR;
    case WellReadStatus.WELL_READ_STATUS_WARNING:
      return WellReadStatusV2.WARNING;
    case WellReadStatus.WELL_READ_STATUS_SUCCESS:
      return WellReadStatusV2.SUCCESS;
    case WellReadStatus.WELL_READ_STATUS_NO_DATA:
      return WellReadStatusV2.NO_DATA;
    case WellReadStatus.WELL_READ_STATUS_UNEXPECTED_JOB:
      return WellReadStatusV2.UNEXPECTED_JOB;
    case WellReadStatus.UNRECOGNIZED:
      return WellReadStatusV2.ERROR;
    default:
      return WellReadStatusV2.UNSPECIFIED;
  }
}

/**
 * Wrapper for ExperimentEditorContainer (rev2 analysis support)
 */
export default function ExperimentEditor(){
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (location.state == null || location.state == undefined) {
      navigate('/');
    }
  }, [location.state, navigate])
  const experimentId: string = location.state.experimentId;
  const dispatch: AppDispatch = useAppDispatch();
  const plateWellConfigs = useAppSelector(selectAllPlateWellConfigs);

  const uploadAndSave = async (files: File[] | FileList, inputDataFormat: string) => {
    dispatch(updateAnalysisViewSettings({ changes: { loading: {
              experimentLoading: false,
              analysisRunning: false,
              saving: false,
              exporting: false,
              uploading: true,
            }, } }));
    try {
      let uploadResponse: any;
      if (inputDataFormat === 'cytek') {
        const filez: File[] = [files[0]];
        uploadResponse = await UploadCytekFcs(filez);
      } else {
        uploadResponse = await UploadRpck(files);
      }
      const status = uploadResponse?.uploadStatus;
      if (status === RpckUploadStatus.RPCK_UPLOAD_STATUS_ALREADY_UPLOADED || status === RpckUploadStatus.RPCK_UPLOAD_STATUS_NEW_UPLOAD_SUCCESS || status === RpckUploadStatus.RPCK_UPLOAD_STATUS_UPLOADED_WITH_NEW_ID) {
        const rpckDetails = uploadResponse.details;
        const instrumentDataId = rpckDetails?.id;
        const instrumentDataCheck = await dataMgr.getInstrumentData({
          instrumentDataId,
        });
        // If the instrumentData record does not already exist, create it
        if (!instrumentDataCheck?.instrumentData) {
          const reqBody = InstrumentDataBody.fromPartial({
            rpckId: rpckDetails?.id,
            blobAccountName: 'any',
            blobContainerName: rpckDetails?.instrumentSerialNumber,
            blobFileName: rpckDetails?.name,
            instrumentSerialNumber: rpckDetails?.instrumentSerialNumber,
          });
          await dataMgr.writeInstrumentData({
            body: reqBody,
          });
        }
        dispatch(
          addInstrumentData({
            instrumentDataId,
            instrumentDataFormat: inputDataFormat,
          }),
        );
        getInstrumentDataStatus(plateWellConfigs, uploadResponse.jobStatuses.jobStatuses);
        await dispatch(saveExperiment(experimentId));
        // SFSE-1346
        // Async refresh of experiment list
        dispatch(getExperimentList())

        // Reload instrument data after update
        await dispatch(openExperiment(experimentId));
      }
      return uploadResponse;
    } catch (error) {
        console.error(error);
        
        //Throwing error to InstrumentRunViewerContainer
        throw error; 
      } 
      
      finally {
        // Always stop loading
        dispatch(updateAnalysisViewSettings({ changes: { loading: {
                experimentLoading: false,
                analysisRunning: false,
                saving: false,
                exporting: false,
                uploading: false,
              }, } }));
    }
  };

    


  function getInstrumentDataStatus(
    layoutWells: PlateWellConfig[],
    wellReadStatuses: RpckJobStatus[],
  ) {
    const dataStatus: Record<string, Partial<PlateWellConfig>> = {};

    if (wellReadStatuses?.length < 1) {
      return dataStatus;
    }

    wellReadStatuses.forEach(well => {
      const layoutWell = layoutWells.find(w => w.wellAddress === well.wellAddress);
      const wellReadStatusV2 = wellReadStatusMap(well?.wellReadStatus)
      if (
        layoutWell === undefined ||
        layoutWell?.sampleType === SampleType.NONE ||
        layoutWell?.sampleType === SampleType.UNSPECIFIED
      ) {
        if (
          wellReadStatusV2 !== undefined &&
          wellReadStatusV2 !== WellReadStatusV2.NO_DATA
        ) {
          const status = {
            wellReadStatus: WellReadStatusV2.UNEXPECTED_JOB,
          };
          dataStatus[well.wellAddress] = status;
        }
      } else {
        const status = {
          wellReadStatus:
            wellReadStatusV2 === WellReadStatusV2.NO_DATA
              ? WellReadStatusV2.ERROR
              : wellReadStatusV2,
          assayName: layoutWell.assayName || '',
          sampleName: layoutWell?.sampleName || layoutWell.wellAddress,
          wellType: layoutWell?.sampleType,
        };
        dataStatus[well.wellAddress!] = status;
      }
      dispatch(updateWell({wellAddress: well.wellAddress!, newWellConfig: dataStatus[well.wellAddress!]}));
    });

    return dataStatus;
  }

    return (
        <ExperimentEditorContainer experimentId={experimentId} handleUpload={uploadAndSave}/>
    );
}